import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Text,
  HStack,
  VStack,
  Spinner,
  Center,
  Heading,
  TableContainer,
} from "@chakra-ui/react";
import {
  Search,
  MoreVertical,
  ChevronLeft,
  ChevronRight,
  Filter,
  ChevronDown,
} from "lucide-react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAccessCheck } from "@/hooks/useAccess";
import { useToastNotification } from "@/lib/utils/useToastNotification";
import MobileProcedureCard from "@/components/pageBuilder/MobileProcedureCard";
import NewProcedureModal from "@/components/modals/NewProcedureModal";
import ViewProcedureModal from "@/components/modals/ViewProcedureModal";
import { procedureService } from "@/lib/services/procedureService";
import { Procedures } from "@/types/procedures";
import { getStatusColorAndBg } from "@/util/getStatusColor";

export default function ProcedurePage() {
  const navigate = useNavigate();
  const access = useAccessCheck();
  const { showError, showSuccess } = useToastNotification();

  // State
  const [isNewProcedureModalOpen, setIsNewProcedureModalOpen] = useState(false);
  const [isViewProcedureModalOpen, setIsViewProcedureModalOpen] =
    useState(false);
  const [selectedProcedureId, setSelectedProcedureId] = useState<number | null>(
    null,
  );
  const [editingProcedure, setEditingProcedure] = useState<Procedures | null>(
    null,
  );
  const [procedures, setProcedures] = useState<Procedures[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  // Export state
  const [isExporting, setIsExporting] = useState(false);
  const [exportType, setExportType] = useState<"excel" | "csv">("csv");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const permissions = useMemo(
    () => ({
      can_view: access.can_view,
      can_edit: access.can_edit,
      can_create: access.can_create,
      can_delete: access.can_delete,
      can_export: access.can_export,
    }),
    [access],
  );

  // Fetch procedures from API
  const fetchProcedures = async () => {
    setIsLoading(true);
    try {
      const fetchedProcedures = await procedureService.getProcedures();
      setProcedures(fetchedProcedures || []);
    } catch (error: any) {
      showError(error.message || "Failed to fetch procedures");
      setProcedures([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Load procedures on mount
  useEffect(() => {
    fetchProcedures();
  }, []);

  // Handle view action
  const handleViewAction = (procedureId: number) => {
    setSelectedProcedureId(procedureId);
    setIsViewProcedureModalOpen(true);
  };

  const handleEditAction = (procedureId: number) => {
    const procedure = procedures.find(p => p.id === procedureId);
    if (procedure) {
      setEditingProcedure(procedure);
      setIsNewProcedureModalOpen(true);
    }

  };
  const handleDeleteAction = async (procedureId: number) => {
    const procedure = procedures.find(p => p.id === procedureId);
    if (procedure) {
      await procedureService.deleteProcedure(procedureId);
      fetchProcedures();
    }
  };

  // Filter and sort procedures
  const filteredAndSortedProcedures = useMemo(() => {
    let filtered = procedures.filter((procedure) => {
      const matchesSearch =
        procedure.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (procedure.description &&
          procedure.description
            .toLowerCase()
            .includes(searchTerm.toLowerCase()));

      const matchesStatus =
        statusFilter === "all" ||
        String(procedure.status).toLowerCase() === statusFilter.toLowerCase();

      return matchesSearch && matchesStatus;
    });

    // Sort
    filtered.sort((a, b) => {
      let aValue = a[sortBy as keyof Procedures];
      let bValue = b[sortBy as keyof Procedures];

      if (aValue === undefined || aValue === null) aValue = "";
      if (bValue === undefined || bValue === null) bValue = "";

      if (sortBy === "price") {
        aValue = Number(aValue);
        bValue = Number(bValue);
      }

      if (sortBy === "name") {
        const comparison = String(aValue).localeCompare(String(bValue));
        return sortOrder === "asc" ? comparison : -comparison;
      }

      if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
      if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [procedures, searchTerm, statusFilter, sortBy, sortOrder]);

  // Pagination
  const paginatedProcedures = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedProcedures.slice(
      startIndex,
      startIndex + itemsPerPage,
    );
  }, [filteredAndSortedProcedures, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(
    filteredAndSortedProcedures.length / itemsPerPage,
  );

  // Handle save new procedure
  const handleSaveProcedure = async (procedureData: Procedures) => {
    try {
      const priceValue =
        typeof procedureData.price === "string"
          ? parseFloat(procedureData.price)
          : procedureData.price;

      const data = {
        name: procedureData.name,
        description: procedureData.description,
        price: priceValue,
        status:
          procedureData.status === 1 ||
          String(procedureData.status).toLowerCase() === "active"
            ? "active"
            : "inactive",
      };

      await procedureService.createProcedure(data);
      showSuccess("Success", "Procedure created successfully");
      setIsNewProcedureModalOpen(false);
      fetchProcedures();
    } catch (error: any) {
      showError(error.message || "Failed to create procedure");
    }
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Handle sort change
  const handleSortChange = (value: string) => {
    const sortOptions: Record<
      string,
      { sort_by: string; sort_order: "asc" | "desc" }
    > = {
      recent: { sort_by: "created_at", sort_order: "desc" },
      "name-asc": { sort_by: "name", sort_order: "asc" },
      "name-desc": { sort_by: "name", sort_order: "desc" },
      "price-asc": { sort_by: "price", sort_order: "asc" },
      "price-desc": { sort_by: "price", sort_order: "desc" },
    };

    const selected = sortOptions[value];
    if (selected) {
      setSortBy(selected.sort_by);
      setSortOrder(selected.sort_order);
    }
  };

  // Format price for display
  const formatPrice = (price: number) => {
    return `₱${price.toLocaleString()}`;
  };

  // Export functions
  const handleExport = async (type: "excel" | "csv") => {
    setIsExporting(true);
    setExportType(type);

    try {
      // Prepare data for export
      const dataToExport = filteredAndSortedProcedures.map((procedure) => ({
        "Procedure Name": procedure.name,
        Description: procedure.description || "",
        Price: formatPrice(procedure.price),
        Status: procedure.status === "active" ? "Active" : "Inactive",
        "Created Date":
          procedure.created_at || new Date().toISOString().split("T")[0],
      }));

      if (type === "csv") {
        await exportToCSV(
          dataToExport,
          `procedures_${new Date().toISOString().split("T")[0]}.csv`,
        );
      } else if (type === "excel") {
        await exportToExcel(
          dataToExport,
          `procedures_${new Date().toISOString().split("T")[0]}`,
        );
      }

      showSuccess(
        "Success",
        `Procedures exported successfully as ${type.toUpperCase()}`,
      );
    } catch (error: any) {
      showError(error.message || `Failed to export as ${type.toUpperCase()}`);
    } finally {
      setIsExporting(false);
    }
  };

  // Helper functions for export
  const exportToCSV = (data: any[], filename: string) => {
    return new Promise<void>((resolve, reject) => {
      try {
        if (data.length === 0) {
          reject(new Error("No data to export"));
          return;
        }

        const headers = Object.keys(data[0]);
        const csvRows = [
          headers.join(","),
          ...data.map((row) =>
            headers
              .map((header) => {
                const cell = row[header];

                const escaped = ("" + cell).replace(/"/g, '""');
                return /[,"\n]/.test(escaped) ? `"${escaped}"` : escaped;
              })
              .join(","),
          ),
        ];

        const csvContent = csvRows.join("\n");
        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  };

  const exportToExcel = (data: any[], filename: string) => {
    return exportToCSV(data, `${filename}.xlsx`);
  };

  return (
    <AdminLayout>
      <Box
        minH="calc(100vh - 110px)"
        overflow="hidden"
        display="flex"
        flexDirection="column"
        bg="#FEFCF8"
        py={1}
        px={5}
      >
        <VStack spacing={4} align="stretch" flex="1" overflow="auto" minH={0}>
          <Box flexShrink={0} py={3}>
            <Flex
              direction={{ base: "column", lg: "row" }}
              justify="space-between"
              align={{ base: "flex-start", lg: "center" }}
              gap={{ base: 3, lg: 0 }}
              mt={5}
            >
              <Heading size="md">
                <Badge
                  color="#2E37A4"
                  bg="#ECEDF7"
                  fontSize="sm"
                  px={3}
                  py={1}
                  borderRadius="md"
                  fontWeight="medium"
                  variant="outline"
                >
                  Total Procedures : {filteredAndSortedProcedures.length}
                </Badge>
              </Heading>
              <HStack spacing={3} flexWrap="wrap">
                <Menu>
                  <MenuButton
                    as={Button}
                    rightIcon={<ChevronDown size={16} />}
                    variant="outline"
                    size={{ base: "sm", lg: "sm" }}
                    fontSize={{ base: "xs", lg: "sm" }}
                    isDisabled={
                      !permissions.can_export ||
                      isExporting ||
                      filteredAndSortedProcedures.length === 0
                    }
                    bg="white"
                    borderColor="gray.200"
                    _hover={{ borderColor: "gray.300" }}
                    _active={{ bg: "gray.50" }}
                    fontWeight="500"
                    px={4}
                    isLoading={isExporting}
                    loadingText={`Exporting ${exportType.toUpperCase()}...`}
                  >
                    Export
                  </MenuButton>
                  <MenuList fontSize={{ base: "xs", md: "sm" }}>
                    <MenuItem
                      onClick={() => handleExport("excel")}
                      isDisabled={filteredAndSortedProcedures.length === 0}
                    >
                      Export as Excel
                    </MenuItem>
                    <MenuItem
                      onClick={() => handleExport("csv")}
                      isDisabled={filteredAndSortedProcedures.length === 0}
                    >
                      Export as CSV
                    </MenuItem>
                  </MenuList>
                </Menu>

                {permissions.can_create && (
                  <Button
                    onClick={() => setIsNewProcedureModalOpen(true)}
                    bg="#DC3611"
                    _hover={{ bg: "#bf3c1fff" }}
                    color="white"
                    size={{ base: "sm", md: "sm" }}
                    fontSize={{ base: "xs", md: "sm" }}
                  >
                    + New Procedure
                  </Button>
                )}
              </HStack>
            </Flex>
          </Box>
          <Box borderBottom="1px solid #E7E8EB" pb={2} mb={4}></Box>

          <Box
            py={1}
            pt={2}
            pb={2}
            flexShrink={0}
            display="flex"
            justifyContent="space-between"
            flexDirection={{ base: "column", lg: "row" }}
          >
            {/* Left side - Search only */}
            <HStack spacing={4} flexWrap="wrap" alignItems={"center"}>
              <Box flex="0 1 auto" minW="250px" maxW="300px" h="32px">
                <InputGroup size="lg" h="full">
                  <InputLeftElement pointerEvents="none" h="full">
                    <Search size={16} color="#9DA4B0" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    fontSize="14px"
                    h="full"
                    bg="white"
                  />
                </InputGroup>
              </Box>
            </HStack>

            {/* Right side - Filter and Sort By */}
            <HStack
              spacing={4}
              flexWrap="wrap"
              alignItems={"center"}
              className="mt-3 lg:mt-0"
            >
              <Menu>
                <MenuButton
                  as={Button}
                  variant="outline"
                  size={{ base: "sm", lg: "sm" }}
                  fontSize={{ base: "xs", lg: "sm" }}
                  leftIcon={<Filter size={16} />}
                  isDisabled={!permissions.can_view}
                  bg="white"
                >
                  Filter
                </MenuButton>
                <MenuList>
                  <MenuItem onClick={() => setStatusFilter("all")}>
                    All Status
                  </MenuItem>
                  <MenuItem onClick={() => setStatusFilter("active")}>
                    Active
                  </MenuItem>
                  <MenuItem onClick={() => setStatusFilter("inactive")}>
                    Inactive
                  </MenuItem>
                </MenuList>
              </Menu>

              <Menu>
                <MenuButton
                  as={Button}
                  variant="outline"
                  size={{ base: "sm", lg: "sm" }}
                  fontSize={{ base: "xs", lg: "sm" }}
                  rightIcon={<ChevronDown size={16} />}
                  textColor="gray.900"
                  bg="white"
                >
                  Sort By :{" "}
                  {sortBy === "created_at"
                    ? "Recent"
                    : sortBy === "name"
                      ? "Name"
                      : sortBy === "price"
                        ? "Price"
                        : "Recent"}
                </MenuButton>
                <MenuList>
                  <MenuItem onClick={() => handleSortChange("recent")}>
                    Recent
                  </MenuItem>
                  <MenuItem onClick={() => handleSortChange("name-asc")}>
                    Name (A-Z)
                  </MenuItem>
                  <MenuItem onClick={() => handleSortChange("name-desc")}>
                    Name (Z-A)
                  </MenuItem>
                  <MenuItem onClick={() => handleSortChange("price-asc")}>
                    Price (Low to High)
                  </MenuItem>
                  <MenuItem onClick={() => handleSortChange("price-desc")}>
                    Price (High to Low)
                  </MenuItem>
                </MenuList>
              </Menu>
            </HStack>
          </Box>

          <Box
            flex="1"
            overflow="hidden"
            display="flex"
            flexDirection="column"
            minH={0}
            borderRadius="0"
          >
            {isLoading ? (
              <Center flex="1" p={8}>
                <Spinner size="lg" color="blue.500" />
              </Center>
            ) : filteredAndSortedProcedures.length === 0 ? (
              <Center flex="1" p={8}>
                <Text color="gray.500">No procedures found</Text>
              </Center>
            ) : (
              <>
                {/* Mobile View */}
                <Box
                  display={{ base: "block", lg: "none" }}
                  overflowY="auto"
                  flex="1"
                  px={2}
                >
                  {paginatedProcedures.map(
                    (procedure) =>
                      procedure.id && (
                        <MobileProcedureCard
                          key={procedure.id}
                          procedure={{
                            ...procedure,
                            id: procedure.id,
                            price: formatPrice(procedure.price),
                            status:
                              procedure.status === "active"
                                ? "Active"
                                : "Inactive",
                          }}
                          permissions={permissions}
                          onViewDetails={() => handleViewAction(procedure.id!)}
                        />
                      ),
                  )}
                </Box>

                {/* Desktop Table View */}
                <Box
                  display={{ base: "none", lg: "flex" }}
                  flexDirection="column"
                  h="100%"
                  minH={0}
                  border="1px"
                  borderColor="gray.200"
                  overflow="hidden"
                  bg="white"
                >
                  <Box overflowY="auto" flex="1">
                    <TableContainer w="100%" h="100%">
                      <Table variant="simple" size="md" w="100%">
                        <Thead bg="white" position="sticky" top={0} zIndex={1}>
                          <Tr>
                            <Th
                              textTransform="none"
                              fontSize="14px"
                              fontWeight="600"
                              color="#0A1B39"
                              px={4}
                              py={3}
                            >
                              Procedure name
                            </Th>
                            <Th
                              textTransform="none"
                              fontSize="14px"
                              fontWeight="600"
                              color="#0A1B39"
                              px={4}
                              py={3}
                            >
                              Description
                            </Th>
                            <Th
                              textTransform="none"
                              fontSize="14px"
                              fontWeight="600"
                              color="#0A1B39"
                              px={4}
                              py={3}
                            >
                              Price
                            </Th>
                            <Th
                              textTransform="none"
                              fontSize="14px"
                              fontWeight="600"
                              color="#0A1B39"
                              px={4}
                              py={3}
                              width="10px"
                            >
                              Status
                            </Th>
                            {permissions.can_view && (
                              <Th
                                textTransform="none"
                                fontSize="14px"
                                fontWeight="600"
                                color="#0A1B39"
                                px={4}
                                py={3}
                                width="150px"
                              />
                            )}
                          </Tr>
                        </Thead>
                        <Tbody>
                          {paginatedProcedures.map((procedure) => (
                            <Tr key={procedure.id} _hover={{ bg: "gray.50" }}>
                              <Td
                                fontSize="sm"
                                fontWeight="medium"
                                px={4}
                                py={3}
                                verticalAlign="middle"
                              >
                                {procedure.name}
                              </Td>
                              <Td
                                fontSize="sm"
                                color="gray.600"
                                px={4}
                                py={3}
                                verticalAlign="middle"
                              >
                                {procedure.description}
                              </Td>
                              <Td
                                fontSize="sm"
                                fontWeight="semibold"
                                px={4}
                                py={3}
                                verticalAlign="middle"
                              >
                                {formatPrice(procedure.price)}
                              </Td>
                              <Td px={4} py={3} verticalAlign="middle">
                                <Badge
                                  colorScheme={
                                    getStatusColorAndBg(
                                      String(procedure.status) || "",
                                    ).color
                                  }
                                  fontSize="xs"
                                  px={3}
                                  py={1}
                                  borderRadius="md"
                                  variant="outline"
                                  textTransform="none"
                                  bg={
                                    getStatusColorAndBg(
                                      String(procedure.status) || "",
                                    ).bg
                                  }
                                >
                                  {procedure.status === "active"
                                    ? "Active"
                                    : "Inactive"}
                                </Badge>
                              </Td>
                                <Td
                                  px={4}
                                  py={3}
                                  verticalAlign="middle"
                                  width="60px"
                                  >
                                  <Flex align="center" justify="center">
                                    <Menu>
                                      <MenuButton
                                        as={IconButton}
                                        icon={<MoreVertical size={16} />}
                                        variant="outline"
                                        size="sm"
                                        aria-label="Options"
                                      />
                                      <MenuList fontSize="sm">
                                        {permissions.can_view && (
                                          <>
                                              <MenuItem
                                                onClick={() =>
                                                  handleViewAction(procedure.id!)
                                                }
                                              >
                                                View Details
                                              </MenuItem>
                                          </>
                                        )}
                                        {permissions.can_edit && (
                                          <>
                                              <MenuItem
                                                onClick={() =>
                                                  handleEditAction(procedure.id!)
                                                }
                                              >
                                                Edit Procedure
                                              </MenuItem>
                                          </>
                                        )}
                                        {permissions.can_delete && (
                                          <>
                                              <MenuItem
                                                onClick={() =>
                                                  handleDeleteAction(procedure.id!)
                                                }
                                                className="text-red-600"
                                              >
                                                Delete Procedure
                                              </MenuItem>
                                          </>
                                        )}
                                      </MenuList>
                                    </Menu>
                                  </Flex>
                                </Td>
                            </Tr>
                          ))}
                          {/* Add empty rows to maintain consistent height when fewer items */}
                          {paginatedProcedures.length < itemsPerPage &&
                            Array.from({
                              length: itemsPerPage - paginatedProcedures.length,
                            }).map((_, index) => (
                              <Tr key={`empty-${index}`} height="57px">
                                <Td px={4} py={3} verticalAlign="middle"></Td>
                                <Td px={4} py={3} verticalAlign="middle"></Td>
                                <Td px={4} py={3} verticalAlign="middle"></Td>
                                <Td px={4} py={3} verticalAlign="middle"></Td>
                                {permissions.can_view && (
                                  <Td px={4} py={3} verticalAlign="middle"></Td>
                                )}
                              </Tr>
                            ))}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  </Box>

                  {/* Desktop Pagination */}
                  <Box
                    borderTop="1px"
                    borderColor="gray.200"
                    bg="white"
                    p={4}
                    flexShrink={0}
                  >
                    <Flex
                      direction={{ base: "column", sm: "row" }}
                      justify="space-between"
                      align="center"
                      gap={{ base: 3, sm: 0 }}
                    >
                      <Text fontSize="sm" color="gray.600">
                        Showing <b>{paginatedProcedures.length}</b> from{" "}
                        <b>{filteredAndSortedProcedures.length}</b> procedures
                      </Text>
                      <HStack spacing={2}>
                        <IconButton
                          icon={<ChevronLeft size={16} />}
                          aria-label="Previous"
                          variant="outline"
                          size="sm"
                          isDisabled={currentPage === 1}
                          onClick={() => handlePageChange(currentPage - 1)}
                        />
                        {Array.from(
                          { length: Math.min(5, totalPages) },
                          (_, i) => i + 1,
                        ).map((page) => (
                          <Button
                            key={page}
                            size="sm"
                            variant={currentPage === page ? "solid" : "outline"}
                            colorScheme={currentPage === page ? "red" : "gray"}
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </Button>
                        ))}
                        {totalPages > 5 && (
                          <>
                            <Text fontSize="sm" color="gray.600">
                              ...
                            </Text>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handlePageChange(totalPages)}
                            >
                              {totalPages}
                            </Button>
                          </>
                        )}
                        <IconButton
                          icon={<ChevronRight size={16} />}
                          aria-label="Next"
                          variant="outline"
                          size="sm"
                          isDisabled={currentPage === totalPages}
                          onClick={() => handlePageChange(currentPage + 1)}
                        />
                      </HStack>
                    </Flex>
                  </Box>
                </Box>

                {/* Mobile Pagination */}
                <Box
                  display={{ base: "block", lg: "none" }}
                  borderTop="1px"
                  borderColor="gray.200"
                  bg="white"
                  p={4}
                  mt={3}
                >
                  <Flex direction="column" align="center" gap={3}>
                    <Text fontSize="xs" color="gray.600">
                      Showing <b>{paginatedProcedures.length}</b> from{" "}
                      <b>{filteredAndSortedProcedures.length}</b> procedures
                    </Text>
                    <HStack spacing={2}>
                      <IconButton
                        icon={<ChevronLeft size={16} />}
                        aria-label="Previous"
                        variant="outline"
                        size="sm"
                        isDisabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                      />
                      <Text fontSize="sm" fontWeight="medium">
                        {currentPage} / {totalPages || 1}
                      </Text>
                      <IconButton
                        icon={<ChevronRight size={16} />}
                        aria-label="Next"
                        variant="outline"
                        size="sm"
                        isDisabled={currentPage === totalPages}
                        onClick={() => handlePageChange(currentPage + 1)}
                      />
                    </HStack>
                  </Flex>
                </Box>
              </>
            )}
          </Box>
        </VStack>
      </Box>

      <NewProcedureModal
        isOpen={isNewProcedureModalOpen}
        onClose={() => setIsNewProcedureModalOpen(false)}
        onSave={handleSaveProcedure}
        editingProcedure={editingProcedure}
        setProcedures={setProcedures}
      />

      <ViewProcedureModal
        isOpen={isViewProcedureModalOpen}
        onClose={() => {
          setIsViewProcedureModalOpen(false);
          setSelectedProcedureId(null);
        }}
        procedureId={selectedProcedureId}
      />
    </AdminLayout>
  );
}
